import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlayCircle } from "lucide-react";
import TrialButton from "./TrialButton";
import DemoModal from "./DemoModal";
import { trackEvent } from "@/utils/tagManagerUtils";
import { getAuthUrl } from "@/utils/redirectUtils";

const CLAUDE_INSTALL_URL =
  "https://claude.ai/settings/connectors?add=https%3A%2F%2Fapp.upfixe.com%2Fmcp";
const CHATGPT_INSTALL_URL = "https://chatgpt.com/settings/connectors";

const compatibility = [
  { name: "Claude", note: null },
  { name: "ChatGPT", note: "Business / Enterprise / Edu" },
  { name: "Perplexity", note: null },
  { name: "MCP Inspector", note: null },
];

const McpHero = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  const handleDemoClick = () => {
    trackEvent("mcp_demo_open", { source: "hero" });
    setDemoOpen(true);
  };

  return (
    <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-primary/5 to-background">
      <div className="container mx-auto max-w-6xl px-4 py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <Badge variant="secondary" className="mb-6">
            New · Chat-native Google Ads
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
            The first chat-native{" "}
            <span className="text-primary">Google Ads platform</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground">
            Every other Google Ads connector is read-only. Upfixe actually
            builds, edits and publishes your campaigns — by chat, in minutes.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <TrialButton source="hero" />
            <Button variant="outline" size="lg" onClick={handleDemoClick}>
              <PlayCircle className="mr-1 h-4 w-4" />
              Watch a 60-sec demo
            </Button>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Already have an Upfixe account?{" "}
            <a
              href={CLAUDE_INSTALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("mcp_install_claude_click", { source: "hero" })}
              className="text-primary hover:underline"
            >
              Install in Claude
            </a>
            {" · "}
            <a
              href={CHATGPT_INSTALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("mcp_install_chatgpt_click", { source: "hero" })}
              className="text-primary hover:underline"
            >
              Install in ChatGPT
            </a>
            <span className="text-xs"> (Business / Enterprise / Edu)</span>
            {" · "}
            <a
              href={`${getAuthUrl()}/login`}
              onClick={() => trackEvent("mcp_signin_click", { source: "hero" })}
              className="text-primary hover:underline"
            >
              Sign in
            </a>
          </p>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://smithery.ai/servers/george-mvur/upfixe-ads"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("mcp_smithery_click", { source: "hero" })}
              aria-label="Listed on Smithery"
              className="inline-block"
            >
              <img
                src="https://smithery.ai/badge/george-mvur/upfixe-ads"
                alt="Listed on Smithery"
                loading="lazy"
                width={180}
                height={40}
                className="inline-block max-w-none"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </a>

            <a 
              href="https://www.claudeai.directory/showcase/upfixe-ads" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-background hover:bg-muted text-sm font-medium transition-colors shadow-sm"
            >
              <img src="https://www.claudeai.directory/logo-mark.svg" alt="Listed on Claude AI Directory" width={20} height={20} /> 
              Listed on Claude AI Directory
            </a>
          </div>

          <div className="mt-12">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
              Works with
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {compatibility.map((c) => (
                <Badge
                  key={c.name}
                  variant="outline"
                  className="px-3 py-1.5 text-sm font-medium bg-background"
                  title={c.note ?? undefined}
                >
                  {c.name}
                  {c.note && <span className="ml-1 text-muted-foreground">*</span>}
                </Badge>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              * ChatGPT MCP write access currently requires Business / Enterprise
              / Edu (OpenAI's limit, not ours — Claude users are unaffected).
            </p>
          </div>
        </div>
      </div>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </section>
  );
};

export default McpHero;
